const TOKEN_KEYS = ["moo_token", "moo_token_superadmin"];

let authenticatedFetchInstalled = false;
let nativeFetch = null;

const safeLocalStorageGet = (key) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return "";
    return String(window.localStorage.getItem(key) || "").trim();
  } catch (_error) {
    return "";
  }
};

const resolveTokenPriority = ({ tokenMode = "auto", url = "" } = {}) => {
  const isCloudRuntime = () => {
    try {
      if (typeof window === "undefined") return false;
      const host = String(window.location?.hostname || "");
      if (!host) return false;
      if (["localhost", "127.0.0.1", "0.0.0.0", "::1"].includes(host)) return false;
      if (host.endsWith(".local") || host.endsWith(".lan")) return false;
      const match = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
      if (!match) return true;
      const [a, b] = match.slice(1).map((part) => Number(part));
      return !(
        a === 10 ||
        (a === 172 && b >= 16 && b <= 31) ||
        (a === 192 && b === 168) ||
        (a === 169 && b === 254)
      );
    } catch (_error) {
      return false;
    }
  };

  if (isCloudRuntime()) {
    const pathname =
      typeof window !== "undefined" ? String(window.location?.pathname || "") : "";
    const wantsSuperadmin =
      tokenMode === "superadmin" ||
      String(url || "").includes("/api/superadmin") ||
      pathname.startsWith("/superadmin") ||
      pathname.startsWith("/superdamin");
    return ["moo_cloud_token", wantsSuperadmin ? "moo_token_superadmin" : "moo_token"];
  }

  if (tokenMode === "superadmin") {
    return ["moo_token_superadmin"];
  }
  if (tokenMode === "admin") {
    return ["moo_token"];
  }

  const normalizedUrl = String(url || "");
  const pathname =
    typeof window !== "undefined" ? String(window.location?.pathname || "") : "";
  const shouldPreferSuperadmin =
    normalizedUrl.includes("/api/superadmin") ||
    pathname.startsWith("/superadmin") ||
    pathname.startsWith("/superdamin");
  return shouldPreferSuperadmin
    ? ["moo_token_superadmin"]
    : ["moo_token"];
};

export const getStoredAuthToken = ({ tokenMode = "auto", url = "" } = {}) => {
  const orderedKeys = resolveTokenPriority({ tokenMode, url });
  for (const key of orderedKeys) {
    const value = safeLocalStorageGet(key);
    if (value) return value;
  }
  return "";
};

export const clearStoredAuthTokens = ({ tokenMode = "auto", url = "" } = {}) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    resolveTokenPriority({ tokenMode, url })
      .filter((key) => TOKEN_KEYS.includes(key))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch (_error) {
    // Storage can be unavailable in restricted browser contexts.
  }
};

const hasAuthorizationHeader = (headers = {}) => {
  if (!headers) return false;
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    return headers.has("authorization");
  }
  if (Array.isArray(headers)) {
    return headers.some(([key]) => String(key).toLowerCase() === "authorization");
  }
  return Object.keys(headers || {}).some(
    (key) => String(key).toLowerCase() === "authorization",
  );
};

export const buildAuthHeaders = ({
  includeBearer = false,
  headers = {},
  tokenMode = "auto",
  url = "",
} = {}) => {
  const normalizedHeaders = headers || {};
  const hasAuthorization = hasAuthorizationHeader(normalizedHeaders);

  if (!includeBearer || hasAuthorization) {
    return normalizedHeaders;
  }

  const token = getStoredAuthToken({ tokenMode, url });
  if (!token) return normalizedHeaders;
  return { ...(headers || {}), Authorization: `Bearer ${token}` };
};

const isApiUrl = (url) => {
  try {
    if (typeof window === "undefined") return false;
    const rawUrl =
      typeof Request !== "undefined" && url instanceof Request ? url.url : String(url || "");
    if (!rawUrl) return false;
    const parsed = new URL(rawUrl, window.location.origin);
    return parsed.pathname.startsWith("/api/");
  } catch (_error) {
    return false;
  }
};

const withAuthorizationHeader = ({ input, init = {} }) => {
  const requestUrl =
    typeof Request !== "undefined" && input instanceof Request ? input.url : String(input || "");
  const initHeaders = init.headers;
  const requestHeaders =
    typeof Request !== "undefined" && input instanceof Request ? input.headers : null;
  const sourceHeaders = initHeaders || requestHeaders || {};

  if (hasAuthorizationHeader(sourceHeaders)) {
    return { input, init };
  }

  const token = getStoredAuthToken({ tokenMode: "auto", url: requestUrl });
  if (!token) {
    return { input, init };
  }

  const headers =
    typeof Headers !== "undefined" ? new Headers(sourceHeaders) : { ...(sourceHeaders || {}) };
  if (typeof headers.set === "function") {
    headers.set("Authorization", `Bearer ${token}`);
  } else {
    headers.Authorization = `Bearer ${token}`;
  }

  if (typeof Request !== "undefined" && input instanceof Request) {
    return { input: new Request(input, { ...init, headers }), init: {} };
  }

  return { input, init: { ...init, credentials: init.credentials ?? "include", headers } };
};

export const installAuthenticatedFetchInterceptor = () => {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  if (authenticatedFetchInstalled) return;

  nativeFetch = window.fetch.bind(window);
  authenticatedFetchInstalled = true;

  window.fetch = (input, init = {}) => {
    if (!isApiUrl(input)) {
      return nativeFetch(input, init);
    }
    const request = withAuthorizationHeader({ input, init });
    return nativeFetch(request.input, request.init);
  };
};

// Un 401 signifie que l'identité présentée n'est plus authentifiée. Un 403
// signifie au contraire qu'elle est connue mais non autorisée pour l'action.
// Ne jamais effacer une session valide à cause d'un simple refus de droit.
export const isDefinitiveAuthenticationRejection = (status) =>
  Number(status) === 401;

export const apiFetch = async (url, options = {}) => {
  const {
    includeBearer = false,
    tokenMode = "auto",
    clearTokenOn401 = false,
    headers,
    ...rest
  } = options || {};

  const response = await fetch(url, {
    ...rest,
    credentials: rest.credentials ?? "include",
    headers: buildAuthHeaders({ includeBearer, headers, tokenMode, url }),
  });

  // Une route secondaire (heartbeat, liste des sessions, logout, etc.) peut
  // répondre 401 alors que le jeton reste valide, notamment pendant un
  // changement d'écran ou une reprise réseau. Seul l'appelant qui vient de
  // vérifier l'identité autoritaire peut demander explicitement la purge.
  const shouldAutoClear = Boolean(clearTokenOn401);

  if (response.status === 401 && shouldAutoClear) {
    clearStoredAuthTokens({ tokenMode, url });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("moo-auth-refresh"));
    }
  }

  return response;
};
